import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

import { AppSettings } from '../../../app.settings';
import {
  BrandDomainsService,
  BrandDomainTarget,
} from '../../../services/brand-domain/brand-domain.service';
import { UiAlertService } from '../../../services/ui-alert/ui-alert.service';
import { AddBrandDomainDialogComponent } from './add-brand-domain-dialog.component';

interface BrandDomainRow {
  domain: string;
  provider: string[];
}

interface AddBrandDomainDialogResult {
  subDomain: string;
  target?: BrandDomainTarget;
}

@Component({
    selector: 'app-brand-domains',
    templateUrl: './brand-domains.component.html',
    styleUrls: ['./brand-domains.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BrandDomainsComponent implements OnInit {
  domainList: BrandDomainRow[] = [];
  loading = false;

  readonly env = AppSettings.getEnv();
  readonly supportsTargetSelection = this.env === 'qa' || this.env === 'demo';
  readonly isDemoEnv = this.env === 'demo';

  selectedTarget: BrandDomainTarget = this.isDemoEnv ? 'demo' : 'qa';
  selectedTabIndex = this.selectedTarget === 'qa' ? 0 : 1;

  constructor(
    private brandDomainsService: BrandDomainsService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private uiAlertService: UiAlertService,
  ) {}

  ngOnInit(): void {
    this.loadDomains();
  }

  get activeTargetLabel(): string {
    return this.selectedTarget === 'demo' ? 'Demo' : 'QA';
  }

  get emptyMessage(): string {
    if (!this.supportsTargetSelection) {
      return 'There are no items to show.';
    }
    return `There are no ${this.activeTargetLabel} target items to show.`;
  }

  onTargetTabChange(selectedIndex: number): void {
    if (!this.supportsTargetSelection) {
      return;
    }
    const nextTarget: BrandDomainTarget = selectedIndex === 0 ? 'qa' : 'demo';
    if (nextTarget === this.selectedTarget) {
      return;
    }
    if (this.isDemoEnv && nextTarget === 'qa') {
      this.selectedTabIndex = 1;
      return;
    }
    this.selectedTarget = nextTarget;
    this.selectedTabIndex = selectedIndex;
    this.loadDomains();
  }

  async loadDomains(): Promise<void> {
    this.loading = true;
    try {
      await this.brandDomainsService.getInfraMgmtToken();
      const res = await this.brandDomainsService.getBrandDomains(this.getActiveTarget());
      const result: BrandDomainRow[] = [];
      for (const provider of Object.keys(res || {})) {
        for (const domain of res[provider] || []) {
          const existing = result.find((item) => item.domain === domain);
          if (existing) {
            existing.provider.push(provider);
          } else {
            result.push({ domain, provider: [provider] });
          }
        }
      }
      this.domainList = result.sort((a, b) => a.domain.localeCompare(b.domain));
    } catch (error) {
      console.error('Error loading brand domains', error);
      this.snackBar.open('Error loading brand domains', 'Close', { duration: 2000 });
    } finally {
      this.loading = false;
    }
  }

  addBrandDomain(): void {
    const dialogRef = this.dialog.open(AddBrandDomainDialogComponent, {
      width: '520px',
      maxWidth: '95vw',
      data: {
        target: this.getActiveTarget(),
        showTargetContext: this.supportsTargetSelection,
      },
    });
    dialogRef.afterClosed().subscribe(async (newDomain: AddBrandDomainDialogResult | undefined) => {
      if (!newDomain?.subDomain) {
        return;
      }
      try {
        await this.brandDomainsService.createBrandDomain(
          newDomain.subDomain,
          this.getMutationTarget(newDomain.target),
        );
        this.snackBar.open('Domain created successfully', 'Close', { duration: 2000 });
        this.loadDomains();
      } catch (error) {
        console.error('Error creating domain', error);
        this.snackBar.open('Error creating domain', 'Close', { duration: 2000 });
      }
    });
  }

  async deleteDomain(domain: string): Promise<void> {
    const target = this.getActiveTarget();
    const confirmMessage = target
      ? `Are you sure you want to delete ${domain} from ${target.toUpperCase()} target?`
      : `Are you sure you want to delete ${domain}?`;
    const confirm = await this.uiAlertService.presentAlertConfirm(confirmMessage);
    if (!confirm) {
      return;
    }
    try {
      await this.brandDomainsService.deleteBrandDomain(domain, target);
      this.snackBar.open('Domain deleted successfully', 'Close', { duration: 2000 });
      this.loadDomains();
    } catch (error) {
      console.error('Error deleting domain', error);
      this.snackBar.open('Error deleting domain', 'Close', { duration: 2000 });
    }
  }

  private getActiveTarget(): BrandDomainTarget | undefined {
    if (!this.supportsTargetSelection) {
      return undefined;
    }
    return this.selectedTarget;
  }

  private getMutationTarget(targetFromDialog?: BrandDomainTarget): BrandDomainTarget | undefined {
    if (!this.supportsTargetSelection) {
      return undefined;
    }
    return targetFromDialog || this.selectedTarget;
  }
}
